import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API, useAuth } from '../../App';
import AdminLayout from '../../components/AdminLayout';
import { toast } from 'sonner';
import { 
  Plus,
  Edit2,
  Trash2,
  Clock,
  Loader2,
  Database,
  Activity
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import { Checkbox } from '../../components/ui/checkbox';

const AdminLotteries = () => {
  const { token } = useAuth();
  const [lotteries, setLotteries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingLottery, setEditingLottery] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    code: '',
    company: '',
    draw_time: '',
    draw_days: [],
    number_range: { min: 0, max: 99 },
    numbers_per_draw: 3,
    color: '#22c55e'
  });

  const days = ['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom'];

  useEffect(() => {
    fetchLotteries();
  }, []);

  const fetchLotteries = async () => {
    try {
      const res = await axios.get(`${API}/lotteries`);
      setLotteries(res.data);
    } catch (e) {
      toast.error('Error al cargar loterías');
    } finally {
      setLoading(false);
    }
  };

  const openCreateDialog = () => {
    setEditingLottery(null);
    setFormData({
      name: '',
      code: '',
      company: '',
      draw_time: '',
      draw_days: [],
      number_range: { min: 0, max: 99 },
      numbers_per_draw: 3,
      color: '#22c55e'
    });
    setDialogOpen(true);
  };

  const openEditDialog = (lottery) => {
    setEditingLottery(lottery);
    setFormData({
      name: lottery.name,
      code: lottery.code,
      company: lottery.company,
      draw_time: lottery.draw_time,
      draw_days: lottery.draw_days || [],
      number_range: lottery.number_range || { min: 0, max: 99 },
      numbers_per_draw: lottery.numbers_per_draw || 3,
      color: lottery.color || '#22c55e'
    });
    setDialogOpen(true);
  };

  const handleSubmit = async () => {
    try {
      if (editingLottery) {
        await axios.put(`${API}/lotteries/${editingLottery.id}`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Lotería actualizada');
      } else {
        await axios.post(`${API}/lotteries`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Lotería creada');
      }
      setDialogOpen(false);
      fetchLotteries();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al guardar lotería');
    }
  };

  const handleDelete = async (lottery) => {
    if (!window.confirm(`¿Eliminar ${lottery.name}?`)) return;
    try {
      await axios.delete(`${API}/lotteries/${lottery.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Lotería eliminada');
      fetchLotteries();
    } catch (e) {
      toast.error('Error al eliminar lotería');
    }
  };

  const toggleDay = (day) => {
    setFormData(prev => ({
      ...prev,
      draw_days: prev.draw_days.includes(day)
        ? prev.draw_days.filter(d => d !== day)
        : [...prev.draw_days, day]
    }));
  };

  if (loading) {
    return (
      <AdminLayout title="Gestión de Loterías" subtitle="Cargando...">
        <div className="flex items-center justify-center h-64">
          <Loader2 size={40} className="text-amber-400 animate-spin" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title="Gestión de Loterías" subtitle={`${lotteries.length} loterías registradas`}>
      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-6">
        <Card className="bg-gray-900 border-gray-800">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-green-500/20 flex items-center justify-center">
                <Database size={20} className="text-green-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-white">{lotteries.length}</p>
                <p className="text-gray-500 text-xs">Total Loterías</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-gray-900 border-gray-800">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-blue-500/20 flex items-center justify-center">
                <Activity size={20} className="text-blue-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-white">{lotteries.filter(l => l.is_active).length}</p>
                <p className="text-gray-500 text-xs">Activas</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-gray-900 border-gray-800">
          <CardContent className="p-4 flex items-center justify-center">
            <Button onClick={openCreateDialog} className="bg-amber-500 hover:bg-amber-400 text-black">
              <Plus size={18} className="mr-2" />
              Nueva Lotería
            </Button>
          </CardContent>
        </Card>
      </div>

      {/* Lotteries Table */}
      <Card className="bg-gray-900 border-gray-800">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="border-gray-800 hover:bg-transparent">
                <TableHead className="text-gray-400">Lotería</TableHead>
                <TableHead className="text-gray-400">Código</TableHead>
                <TableHead className="text-gray-400">Compañía</TableHead>
                <TableHead className="text-gray-400">Horario</TableHead>
                <TableHead className="text-gray-400">Días</TableHead>
                <TableHead className="text-gray-400 text-right">Acciones</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {lotteries.map((lottery) => (
                <TableRow key={lottery.id} className="border-gray-800 hover:bg-gray-800/50">
                  <TableCell>
                    <div className="flex items-center gap-3">
                      <div 
                        className="w-3 h-3 rounded-full" 
                        style={{ backgroundColor: lottery.color || '#22c55e' }}
                      />
                      <span className="text-white font-medium">{lottery.name}</span>
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge className="bg-gray-700 text-gray-300 font-mono">
                      {lottery.code}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-gray-400">{lottery.company}</TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1 text-gray-400">
                      <Clock size={14} />
                      {lottery.draw_time}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex gap-1">
                      {lottery.draw_days?.slice(0, 4).map((day, i) => (
                        <Badge key={i} variant="outline" className="text-xs text-gray-400 border-gray-600">
                          {day.slice(0, 2)}
                        </Badge>
                      ))}
                      {lottery.draw_days?.length > 4 && (
                        <Badge variant="outline" className="text-xs text-gray-400 border-gray-600">
                          +{lottery.draw_days.length - 4}
                        </Badge>
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => openEditDialog(lottery)}
                        className="text-blue-400 hover:text-blue-300 hover:bg-blue-500/10"
                      >
                        <Edit2 size={16} />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDelete(lottery)}
                        className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                      >
                        <Trash2 size={16} />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-lg bg-gray-900 border-gray-700">
          <DialogHeader>
            <DialogTitle className="text-white">
              {editingLottery ? 'Editar Lotería' : 'Nueva Lotería'}
            </DialogTitle>
            <DialogDescription className="text-gray-400">
              {editingLottery ? 'Modifica los datos de la lotería' : 'Agrega una nueva lotería al sistema'}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4 max-h-[60vh] overflow-y-auto">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-gray-300">Nombre</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  placeholder="Nacional"
                  className="bg-gray-800 border-gray-700 text-white"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-gray-300">Código</Label>
                <Input
                  value={formData.code}
                  onChange={(e) => setFormData({...formData, code: e.target.value})}
                  placeholder="NAC"
                  className="bg-gray-800 border-gray-700 text-white"
                />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-gray-300">Compañía</Label>
                <Input
                  value={formData.company}
                  onChange={(e) => setFormData({...formData, company: e.target.value})}
                  placeholder="Lotería Nacional"
                  className="bg-gray-800 border-gray-700 text-white"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-gray-300">Hora de Sorteo</Label>
                <Input
                  value={formData.draw_time}
                  onChange={(e) => setFormData({...formData, draw_time: e.target.value})}
                  placeholder="21:00"
                  className="bg-gray-800 border-gray-700 text-white"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-gray-300">Días de Sorteo</Label>
              <div className="flex flex-wrap gap-2">
                {days.map((day) => (
                  <button
                    key={day}
                    type="button"
                    onClick={() => toggleDay(day)}
                    className={`px-3 py-1.5 rounded-lg text-sm transition-colors ${
                      formData.draw_days.includes(day)
                        ? 'bg-amber-500/20 text-amber-400 border border-amber-500/30'
                        : 'bg-gray-800 text-gray-400 border border-gray-700 hover:bg-gray-700'
                    }`}
                  >
                    {day}
                  </button>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label className="text-gray-300">Mínimo</Label>
                <Input
                  type="number"
                  value={formData.number_range.min}
                  onChange={(e) => setFormData({...formData, number_range: {...formData.number_range, min: parseInt(e.target.value)}})}
                  className="bg-gray-800 border-gray-700 text-white"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-gray-300">Máximo</Label>
                <Input
                  type="number"
                  value={formData.number_range.max}
                  onChange={(e) => setFormData({...formData, number_range: {...formData.number_range, max: parseInt(e.target.value)}})}
                  className="bg-gray-800 border-gray-700 text-white"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-gray-300">Cant. Números</Label>
                <Input
                  type="number"
                  value={formData.numbers_per_draw}
                  onChange={(e) => setFormData({...formData, numbers_per_draw: parseInt(e.target.value)})}
                  className="bg-gray-800 border-gray-700 text-white"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-gray-300">Color</Label>
              <div className="flex items-center gap-3">
                <input
                  type="color"
                  value={formData.color}
                  onChange={(e) => setFormData({...formData, color: e.target.value})}
                  className="w-12 h-10 rounded cursor-pointer"
                />
                <Input
                  value={formData.color}
                  onChange={(e) => setFormData({...formData, color: e.target.value})}
                  className="flex-1 bg-gray-800 border-gray-700 text-white font-mono"
                />
              </div>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDialogOpen(false)} className="text-gray-400">
              Cancelar
            </Button>
            <Button onClick={handleSubmit} className="bg-amber-500 hover:bg-amber-400 text-black">
              {editingLottery ? 'Guardar Cambios' : 'Crear Lotería'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
};

export default AdminLotteries;
